/**
 * 跨站链接的唯一来源。
 *
 * 本地开发：默认指向各站点端口。
 * 离线打包：由构建脚本注入 VITE_SITE_URLS（如 { "media": "../media/" }），
 *          使一套静态产物内各站点之间可以互相跳转。
 */
export interface SiteLink {
  key: string;
  label: string;
  url: string;
}

/**
 * 开发期兜底地址。
 *
 * 离线构建（VITE_OFFLINE=1）时置空：条件在构建期即为常量，打包器会把这个对象字面量
 * 作为死代码消除，产物里不会残留 localhost 端口——否则离线包会隐含"回落到本机端口"的假设。
 */
const DEFAULTS: Record<string, string> = import.meta.env.VITE_OFFLINE
  ? {}
  : {
      group: 'http://127.0.0.1:22661',
      media: 'http://127.0.0.1:22662',
      scenic: 'http://127.0.0.1:22663',
      health: 'http://127.0.0.1:22664',
      dataroom: 'http://127.0.0.1:22665',
      tech: 'http://127.0.0.1:22666',
      invest: 'http://127.0.0.1:22667',
      rural: 'http://127.0.0.1:22668',
    };

/** 站点双语名（页眉、页脚需要 BiText） */
const NAMES: Record<string, { zh: string; en: string }> = {
  group: { zh: '集团门户', en: 'Group' },
  media: { zh: '文化传媒', en: 'Media' },
  scenic: { zh: '景区运营', en: 'Scenic Operations' },
  health: { zh: '康养', en: 'Wellness' },
  dataroom: { zh: '数据室', en: 'Data Room' },
  tech: { zh: '智慧旅游', en: 'Smart Tourism' },
  invest: { zh: '产业投资', en: 'Investment' },
  rural: { zh: '乡村旅游', en: 'Rural Tourism' },
};

/** 站点双语名 */
export function siteName(key: string): { zh: string; en: string } {
  return NAMES[key] ?? { zh: key, en: key };
}

const LABELS: Record<string, string> = {
  group: '集团门户 / Group',
  media: '文化传媒 / Media',
  scenic: '景区运营 / Scenic Operations',
  health: '康养 / Wellness',
  dataroom: '数据室 / Data Room',
  tech: '智慧旅游 / Smart Tourism',
  invest: '产业投资 / Investment',
  rural: '乡村旅游 / Rural Tourism',
};

function overrides(): Record<string, string> {
  const raw = import.meta.env.VITE_SITE_URLS as string | undefined;
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
}

export function siteUrl(key: string): string {
  const o = overrides();
  return o[key] ?? DEFAULTS[key] ?? '#';
}

/**
 * 跨站链接（带上当前语言）。
 *
 * 站点语言存在 hash 前缀里（`#/vi/...`），因此跨站跳转必须把语言一并带过去，
 * 否则用户从 B 站回到 A 站会退回默认语言——这是实际出现过的问题。
 */
export function siteUrlFor(key: string, locale: string, hashPath = ''): string {
  const base = siteUrl(key);
  const clean =
    hashPath && hashPath !== '/'
      ? hashPath.startsWith('/')
        ? hashPath
        : `/${hashPath}`
      : '';
  return `${base}#/${locale}${clean}`;
}

/** 离线打包时被排除的站点（如数据室需要服务端，静态产物无法承载） */
function hidden(): string[] {
  const raw = import.meta.env.VITE_HIDE_SITES as string | undefined;
  return raw ? raw.split(',').map((x) => x.trim()).filter(Boolean) : [];
}

/**
 * 站群之外的外部站点（独立域名，不参与 hash 语言前缀拼接）。
 * 例：票酷 TicketsKu 部署在 t.hastur.cn，是文化传媒板块的票务平台，不属于官网群。
 */
const EXTERNAL_URLS: Record<string, string> = {
  ticketsku: 'https://t.hastur.cn',
};

/**
 * 外部站点地址。
 * 与站内站点不同，外部站点不拼 `#/<locale>`；被 VITE_HIDE_SITES 隐藏时返回空串，
 * 调用方据此不渲染入口（离线包即以此排除外部链接，保持"零外部依赖"）。
 */
export function externalSiteUrl(key: string): string {
  if (hidden().includes(key)) return '';
  return EXTERNAL_URLS[key] ?? '';
}

/** 除自己以外的站点链接（用于导航栏）。传入 locale 时链接会带上该语言。 */
export function otherSites(self: string, locale?: string): SiteLink[] {
  const hid = hidden();
  return Object.keys(LABELS)
    .filter((k) => k !== self && !hid.includes(k))
    .map((k) => ({ key: k, label: LABELS[k], url: locale ? siteUrlFor(k, locale) : siteUrl(k) }));
}
